<script setup lang="ts">
// -----------------------
// props & emits
// -----------------------
const props = defineProps<{
  isLoading: boolean;
}>();

const emit = defineEmits<{
  send: [content: string];
}>();

// -----------------------
// reactive properties
// -----------------------
const message = ref("");
const textareaRef = ref<HTMLTextAreaElement>();

// -----------------------
// helper functions
// -----------------------
const handleSend = () => {
  const trimmedMessage = message.value.trim();
  if (trimmedMessage && !props.isLoading) {
    emit("send", trimmedMessage);
    message.value = "";
    // Reset textarea height
    if (textareaRef.value) {
      textareaRef.value.style.height = "auto";
    }
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    handleSend();
  }
};

const adjustTextareaHeight = () => {
  if (textareaRef.value) {
    textareaRef.value.style.height = "auto";
    textareaRef.value.style.height = `${Math.min(textareaRef.value.scrollHeight, 120)}px`;
  }
};

// Watch for message changes to adjust height
watch(message, adjustTextareaHeight);
</script>

<template>
  <div class="chat-input">
    <div class="flex items-start space-x-2">
      <!-- Textarea -->
      <div class="flex-1 relative">
        <textarea
          ref="textareaRef"
          v-model="message"
          :disabled="isLoading"
          :placeholder="$t('chat.ask_about_products')"
          class="w-full resize-none border border-gray-300 rounded-lg px-3 py-2 pb-6 focus:outline-none focus:ring-2 focus:ring-brand-highlight focus:border-transparent disabled:opacity-50"
          rows="1"
          maxlength="500"
          @keydown="handleKeyDown"
        />
        <div class="absolute bottom-2 right-2 text-xs text-gray-400">
          {{ message.length }}/500
        </div>
      </div>

      <!-- Send button -->
      <button
        :disabled="!message.trim() || isLoading"
        class="bg-brand-primary text-white rounded-lg px-4 py-2 hover:bg-brand-headline disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 h-[44px] flex-shrink-0"
        @click="handleSend"
      >
        <span v-if="!isLoading">{{ $t("chat.send") }}</span>
        <div
          v-else
          class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
        ></div>
      </button>
    </div>
  </div>
</template>

<style scoped>
.chat-input textarea {
  min-height: 44px;
  max-height: 120px;
}
</style>
